import { useEffect, useMemo, useRef, useState } from "react";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2, PlusCircle, Search, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AssignRoleScopeInput,
  type User,
  type UserRoleAssignment,
  dataApi,
  userApi,
} from "@/lib/api/admin-api";
import { organizationApi } from "@/lib/api/services/organization.api";
import { locationApi } from "@/lib/api/services/location.api";
import { VirtualSelectField } from "@/components/common/VirtualSelectField";
import type { Location } from "@/types/location";
import type { Organization } from "@/types/organization";
import { useAuth } from "@/hooks/useAuth";
import { APP_TIMEZONE_OPTIONS } from "@/lib/config/timezones";
import { formatUsPhone } from "@/lib/phone";
import {
  CONCEPT_ADMIN_ROLE_CODES,
  isConceptAdminRoleCode,
  isConceptAdminRoleName,
} from "@/lib/access-control/concept-admin";

const ALL_COMPANIES_VALUE = "all-companies";
const ADMIN_ROLE_PATTERN = /ADMIN/;
const ROLES_REQUIRING_COMPANY = new Set(["CHEF", ...CONCEPT_ADMIN_ROLE_CODES, "LOCATION_MANAGER"]);
const PRIVILEGED_ROLE_CODES = new Set(["SUPER_ADMIN", "SUPPORT"]);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_DIGITS_REGEX = /^\d{7,15}$/;

type UserFormState = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  organizationId: string;
  companyId: string;
  locationId: string;
  roleId: string;
  status: "active" | "inactive";
};

function getInitialFormState(organizationId?: number, companyId?: number): UserFormState {
  return {
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    organizationId: organizationId ? String(organizationId) : "",
    companyId: companyId ? String(companyId) : "",
    locationId: "",
    roleId: "",
    status: "active",
  };
}

function roleRequiresCompany(role?: { code?: string; name?: string }): boolean {
  if (!role) {
    return false;
  }

  if (role.code && ROLES_REQUIRING_COMPANY.has(role.code)) {
    return true;
  }

  const normalizedName = role.name?.trim().toLowerCase();
  return normalizedName === "chef" || isConceptAdminRoleName(role.name) || normalizedName === "location manager";
}

function isPrivilegedRole(role?: { code?: string; name?: string }): boolean {
  if (!role) {
    return false;
  }

  if (role.code && PRIVILEGED_ROLE_CODES.has(role.code)) {
    return true;
  }

  const normalizedName = role.name?.trim().toLowerCase();
  return normalizedName === "super admin" || normalizedName === "support";
}

function sanitizePhoneNumber(value: string): string {
  return value.replace(/\D/g, "");
}

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim());
}

function isValidPhoneNumber(value: string): boolean {
  const phone = value.trim();
  if (!phone) {
    return true;
  }

  return PHONE_DIGITS_REGEX.test(phone);
}

export function SectionRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-stretch border-b border-(--shell-border)">
      <div className="w-[240px] shrink-0 bg-(--color-surface-accent) p-5">
        <p className="text-base font-medium text-(--color-text-primary)">{title}</p>
        <p className="mt-2 text-sm leading-[1.2] text-(--color-text-secondary)">{description}</p>
      </div>
      <div className="min-w-0 flex-1 p-5">{children}</div>
    </div>
  );
}

export function FormLabel({ children, htmlFor }: { children: React.ReactNode; htmlFor?: string }) {
  return (
    <Label htmlFor={htmlFor} className="text-sm font-medium text-(--color-text-primary)">
      {children}
    </Label>
  );
}

export const userDialogInputClass = "";

export const userDialogSelectTriggerClass = "";

function OrganizationLookupSelect({
  value,
  displayValue,
  adminScope,
  onChange,
  onClear,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  triggerId,
  triggerClassName,
}: {
  value: number | null;
  displayValue?: string;
  adminScope?: boolean;
  onChange: (organization: Organization) => void;
  onClear?: () => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  disabled?: boolean;
  /** Id for the trigger button, so a `<label htmlFor>` can point at this field. */
  triggerId?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ["organization-lookup", adminScope ?? null, search],
    queryFn: () =>
      organizationApi.paginate(
        { page: 1, limit: 20, search: search.trim() || undefined },
        { adminScope },
      ),
    enabled: open && !disabled,
  });

  const options = data?.data ?? [];

  useEffect(() => {
    if (!open) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={triggerId}
        disabled={disabled}
        onClick={() => {
          setOpen((prev) => !prev);
          setSearch("");
        }}
        className={`flex h-10 w-full items-center justify-between rounded-md border border-(--color-border) bg-(--color-surface-content) px-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${triggerClassName ?? ""}`}
      >
        <span className={displayValue ? "truncate text-(--color-text-primary)" : "truncate text-(--color-text-secondary)"}>
          {displayValue || placeholder}
        </span>
        <div className="flex items-center gap-2">
          {value != null && onClear ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  onClear();
                }
              }}
              className="text-(--color-text-secondary) hover:text-(--color-text-primary)"
            >
              <X className="h-4 w-4" />
            </span>
          ) : null}
          <ChevronDown className={`h-4 w-4 text-(--color-text-secondary) ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open ? (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-(--color-border) bg-(--color-surface-content) shadow-md">
          <div className="flex items-center gap-2 border-b border-(--color-border) px-3 py-2">
            <Search className="h-4 w-4 text-(--color-text-secondary)" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              autoFocus
              className="h-7 border-none p-0 text-sm shadow-none focus-visible:ring-0"
            />
          </div>

          <div className="max-h-56 overflow-y-auto py-1">
            {options.length ? (
              options.map((organization) => (
                <button
                  key={organization.id}
                  type="button"
                  className="w-full px-3 py-2 text-left text-sm text-(--color-text-primary) hover:bg-(--color-surface-muted)"
                  onClick={() => {
                    onChange(organization);
                    setOpen(false);
                  }}
                >
                  {organization.name}
                </button>
              ))
            ) : (
              <p className="px-3 py-3 text-sm text-(--color-text-secondary)">{emptyMessage}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function CreateUserDialog() {
  const { t } = useLingui();
  const { user, hasRole, hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const isSuperAdmin = hasRole("SUPER_ADMIN");
  const isSupport = hasRole("SUPPORT");
  const isOrgAdmin = hasRole("ORG_ADMIN");
  const isCompanyAdmin = CONCEPT_ADMIN_ROLE_CODES.some((code) => hasRole(code));
  const canAssignAnyOrganization = isSuperAdmin || (isSupport && hasPermission("user:create"));
  const fixedOrganizationId = !canAssignAnyOrganization ? user?.organization?.id : undefined;
  const fixedCompanyId = useMemo(() => {
    if (!isCompanyAdmin) {
      return undefined;
    }

    const scopedCompanyId = user?.roles
      ?.find((role) => isConceptAdminRoleCode(role.code) && role.scopeType === "COMPANY")
      ?.scopeId;

    if (scopedCompanyId) {
      const parsed = Number(scopedCompanyId);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const legacyCompanyId = user?.company?.id;
    if (legacyCompanyId) {
      const parsed = Number(legacyCompanyId);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return undefined;
  }, [isCompanyAdmin, user?.company?.id, user?.roles]);

  const [formData, setFormData] = useState<UserFormState>(
    getInitialFormState(fixedOrganizationId, fixedCompanyId),
  );
  const [orgDisplayName, setOrgDisplayName] = useState("");

  const selectedOrganizationId = useMemo(() => {
    if (formData.organizationId) {
      return Number(formData.organizationId);
    }
    return fixedOrganizationId;
  }, [fixedOrganizationId, formData.organizationId]);

  const selectedCompanyId = useMemo(() => {
    if (isCompanyAdmin) {
      return fixedCompanyId;
    }

    if (formData.companyId && formData.companyId !== ALL_COMPANIES_VALUE) {
      return Number(formData.companyId);
    }

    return undefined;
  }, [fixedCompanyId, formData.companyId, isCompanyAdmin]);

  const { data: companies = [] } = useQuery({
    queryKey: ["organizations", selectedOrganizationId, "companies"],
    queryFn: () => dataApi.getOrganizationCompanies(selectedOrganizationId!),
    enabled: open && !!selectedOrganizationId,
  });

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies],
  );

  const [locationDisplayName, setLocationDisplayName] = useState("");

  const { data: roles = [] } = useQuery({
    queryKey: ["roles", "create-user"],
    queryFn: () => dataApi.getRoles(),
    enabled: open,
  });

  const visibleRoles = useMemo(() => {
    if (isCompanyAdmin) {
      return roles.filter(
        (role) => !ADMIN_ROLE_PATTERN.test(role.code),
      );
    }

    return roles;
  }, [isCompanyAdmin, roles]);

  const selectedRole = useMemo(
    () => visibleRoles.find((role) => String(role.id) === formData.roleId),
    [formData.roleId, visibleRoles],
  );
  const isPrivilegedRoleSelection = isPrivilegedRole(selectedRole);

  const createUserMutation = useMutation({
    mutationFn: async () => {
      const createdUser = await dataApi.createUser({
        email: formData.email.trim(),
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim() || undefined,
        organizationId: isPrivilegedRoleSelection ? undefined : selectedOrganizationId,
        companyId:
          isPrivilegedRoleSelection
            ? undefined
            : isCompanyAdmin
            ? fixedCompanyId
            : formData.companyId && formData.companyId !== ALL_COMPANIES_VALUE
              ? Number(formData.companyId)
              : undefined,
        isActive: formData.status === "active",
        roleId: undefined,
      });

      const selectedRoleId = formData.roleId
        ? Number(formData.roleId)
        : undefined;

      if (selectedRoleId) {
        const scopes: AssignRoleScopeInput[] | undefined = isPrivilegedRoleSelection
          ? undefined
          : formData.locationId
            ? [{ scopeType: "LOCATION", scopeId: Number(formData.locationId) }]
            : selectedCompanyId
              ? [{ scopeType: "COMPANY", scopeId: Number(selectedCompanyId) }]
              : selectedOrganizationId
                ? [{ scopeType: "ORGANIZATION", scopeId: Number(selectedOrganizationId) }]
                : undefined;

        await userApi.assignRole(String(createdUser.id), String(selectedRoleId), scopes);
      }

      return createdUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["users", "all"] });
      queryClient.invalidateQueries({ queryKey: ["users", "stats"] });
      setOpen(false);
      setFormData(getInitialFormState(fixedOrganizationId, fixedCompanyId));
      setOrgDisplayName("");
      setLocationDisplayName("");
      toast.success(t`User created successfully`);
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to create user`);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim() || !formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error(t`Email, first name, and last name are required`);
      return;
    }

    if (!isValidEmail(formData.email)) {
      toast.error(t`Please enter a valid email address`);
      return;
    }

    if (!isValidPhoneNumber(formData.phone)) {
      toast.error(t`Phone number must contain only digits and be between 7 and 15 characters`);
      return;
    }

    if (!formData.roleId) {
      toast.error(t`Role is required`);
      return;
    }

    if (!selectedOrganizationId && !isPrivilegedRoleSelection) {
      toast.error(t`Organization is required`);
      return;
    }

    if (!isPrivilegedRoleSelection && roleRequiresCompany(selectedRole) && !selectedCompanyId) {
      toast.error(t`Concept is required for the selected role`);
      return;
    }

    if (isCompanyAdmin && !fixedCompanyId) {
      toast.error(t`Concept is required for concept admins`);
      return;
    }

    if (
      isOrgAdmin &&
      !isPrivilegedRoleSelection &&
      (!formData.companyId || formData.companyId === ALL_COMPANIES_VALUE)
    ) {
      toast.error(t`Concept is required for organization admins`);
      return;
    }

    createUserMutation.mutate();
  };

  const inputClass = userDialogInputClass;
  const selectTriggerClass = userDialogSelectTriggerClass;

  return (
    <>
      <Button
        className="gap-2"
        onClick={() => {
          setFormData(getInitialFormState(fixedOrganizationId, fixedCompanyId));
          setOrgDisplayName("");
          setLocationDisplayName("");
          setOpen(true);
        }}
      >
        <PlusCircle className="h-4 w-4" />
        {t`Create New User`}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setFormData(getInitialFormState(fixedOrganizationId, fixedCompanyId));
            setOrgDisplayName("");
            setLocationDisplayName("");
          }
        }}
      >
        <DialogContent
          hideClose
          className="max-w-[900px] overflow-hidden rounded-(--radius-lg) border border-(--shell-border) p-0"
        >
          <form onSubmit={onSubmit} className="max-h-[90vh] overflow-y-auto bg-(--color-surface-content)">
            <div className="flex items-start justify-between border-b border-(--shell-border) p-5">
              <div>
                <DialogTitle className="text-2xl font-medium leading-none text-(--color-text-primary)">
                  {t`Add User`}
                </DialogTitle>
                <p className="mt-2 text-base text-(--color-text-secondary)">
                  {t`Create an internal user and configure role and access scope.`}
                </p>
              </div>
              <DialogClose
                aria-label={t`Close dialog`}
                className="text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
              >
                <X className="h-6 w-6" />
              </DialogClose>
            </div>

            <SectionRow
              title={t`General details`}
              description={t`Basic personal details for the new user.`}
            >
              <div className="space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <FormLabel htmlFor="create-user-first-name">{t`First name`}</FormLabel>
                    <Input
                      id="create-user-first-name"
                      value={formData.firstName}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, firstName: e.target.value }))
                      }
                      placeholder={t`Enter first name...`}
                      className={inputClass}
                      autoFocus
                    />
                  </div>
                  <div className="space-y-2">
                    <FormLabel htmlFor="create-user-last-name">{t`Last name`}</FormLabel>
                    <Input
                      id="create-user-last-name"
                      value={formData.lastName}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, lastName: e.target.value }))
                      }
                      placeholder={t`Enter last name...`}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <FormLabel htmlFor="create-user-email">{t`Email`}</FormLabel>
                    <Input
                      id="create-user-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder={t`Enter user email...`}
                      className={inputClass}
                    />
                  </div>
                  <div className="space-y-2">
                    <FormLabel htmlFor="create-user-phone">{t`Phone`}</FormLabel>
                    <Input
                      id="create-user-phone"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, phone: sanitizePhoneNumber(e.target.value) }))
                      }
                      inputMode="numeric"
                      pattern="[0-9]*"
                      placeholder={t`Enter phone...`}
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>
            </SectionRow>

            <SectionRow
              title={t`Role configuration`}
              description={t`Assign role and account status.`}
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="create-user-role">{t`Role`}</FormLabel>
                  <Select
                    value={formData.roleId || "none"}
                    disabled={false}
                    onValueChange={(value) => {
                      const roleId = value === "none" ? "" : value;
                      const nextRole = visibleRoles.find((role) => String(role.id) === roleId);
                      const shouldClearScope = isPrivilegedRole(nextRole);

                      if (shouldClearScope) {
                        setOrgDisplayName("");
                        setLocationDisplayName("");
                      }

                      setFormData((prev) => ({
                        ...prev,
                        roleId,
                        organizationId: shouldClearScope ? "" : prev.organizationId,
                        companyId: shouldClearScope ? "" : prev.companyId,
                        locationId: shouldClearScope ? "" : prev.locationId,
                      }));
                    }}
                  >
                    <SelectTrigger id="create-user-role" className={selectTriggerClass}>
                      <SelectValue placeholder={t`Select role`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t`No role`}</SelectItem>
                      {visibleRoles.map((role) => (
                        <SelectItem key={role.id} value={String(role.id)}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="create-user-status">{t`Status`}</FormLabel>
                  <Select
                    value={formData.status}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        status: value === "inactive" ? "inactive" : "active",
                      }))
                    }
                  >
                    <SelectTrigger id="create-user-status" className={selectTriggerClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">{t`Active`}</SelectItem>
                      <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <FormLabel htmlFor="create-user-organization">{t`Organization`}</FormLabel>
                {canAssignAnyOrganization ? (
                  <OrganizationLookupSelect
                    triggerId="create-user-organization"
                    value={formData.organizationId ? Number(formData.organizationId) : null}
                    adminScope={true}
                    displayValue={orgDisplayName || undefined}
                    onChange={(organization) => {
                      setOrgDisplayName(organization.name);
                      setLocationDisplayName("");
                      setFormData((prev) => ({
                        ...prev,
                        organizationId: String(organization.id),
                        companyId: "",
                        locationId: "",
                      }));
                    }}
                    onClear={() => {
                      setOrgDisplayName("");
                      setLocationDisplayName("");
                      setFormData((prev) => ({
                        ...prev,
                        organizationId: "",
                        companyId: "",
                        locationId: "",
                      }));
                    }}
                    placeholder={t`Select organization`}
                    searchPlaceholder={t`Search organizations…`}
                    emptyMessage={t`No organizations found`}
                    disabled={isPrivilegedRoleSelection}
                    triggerClassName={selectTriggerClass}
                  />
                ) : (
                  <Select value={String(fixedOrganizationId ?? "none")} disabled>
                    <SelectTrigger id="create-user-organization" className={selectTriggerClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={String(fixedOrganizationId ?? "none")}>
                        {user?.organization?.name ?? t`Organization unavailable`}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
            </SectionRow>

            <SectionRow
              title={t`Access scope`}
              description={t`Define concept and location scope for this user.`}
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="create-user-concept">{t`Concept`}</FormLabel>
                  <div className="relative">
                    <Select
                    value={
                      isCompanyAdmin
                        ? String(fixedCompanyId ?? "none")
                        : formData.companyId || "none"
                    }
                    disabled={isPrivilegedRoleSelection || !selectedOrganizationId || isCompanyAdmin}
                    onValueChange={(value) => {
                      setLocationDisplayName("");
                      setFormData((prev) => ({
                        ...prev,
                        companyId: value === "none" ? "" : value,
                        locationId: "",
                      }));
                    }}
                  >
                    <SelectTrigger id="create-user-concept" className={selectTriggerClass}>
                      <SelectValue
                        placeholder={
                          selectedOrganizationId
                            ? isCompanyAdmin
                              ? t`Concept unavailable`
                              : isOrgAdmin
                              ? t`Select concept`
                              : t`Select concept (optional)`
                            : t`Select organization first`
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {!isOrgAdmin && !isCompanyAdmin && (
                        <SelectItem value={ALL_COMPANIES_VALUE}>{t`All concepts`}</SelectItem>
                      )}
                      {!isOrgAdmin && !isCompanyAdmin && <SelectSeparator />}
                      {isCompanyAdmin ? (
                        fixedCompanyId ? (
                          (() => {
                            const fixedCompany = sortedCompanies.find(
                              (company) => company.id === fixedCompanyId,
                            );
                            return (
                              <SelectItem value={String(fixedCompanyId)}>
                                {fixedCompany?.name ?? t`Concept unavailable`}
                              </SelectItem>
                            );
                          })()
                        ) : (
                          <SelectItem value="none">{t`Concept unavailable`}</SelectItem>
                        )
                      ) : (
                        sortedCompanies.map((company) => (
                          <SelectItem key={company.id} value={String(company.id)}>
                            {company.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {!isCompanyAdmin && formData.companyId && formData.companyId !== ALL_COMPANIES_VALUE ? (
                    <button
                      type="button"
                      aria-label={t`Clear concept selection`}
                      className="absolute right-8 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-primary)"
                      onClick={(event) => {
                        event.stopPropagation();
                        setLocationDisplayName("");
                        setFormData((prev) => ({
                          ...prev,
                          companyId: "",
                          locationId: "",
                        }));
                      }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
                </div>

                <div className="space-y-2">
                  <FormLabel htmlFor="create-user-location">{t`Location`}</FormLabel>
                  <VirtualSelectField<Location>
                    triggerId="create-user-location"
                    queryKeyBase={["companies", selectedCompanyId, "locations"]}
                    buildQueryFn={(search) => (page, size) =>
                      locationApi.paginate({
                        page,
                        limit: size,
                        companyId: selectedCompanyId,
                        isActive: true,
                        search: search || undefined,
                      })
                    }
                    getItemValue={(l) => String(l.id)}
                    getItemLabel={(l) => l.name}
                    value={formData.locationId}
                    displayValue={locationDisplayName}
                    onChange={(value, location) => {
                      setLocationDisplayName(location.name);
                      setFormData((prev) => ({ ...prev, locationId: value }));
                    }}
                    onClear={() => {
                      setLocationDisplayName("");
                      setFormData((prev) => ({ ...prev, locationId: "" }));
                    }}
                    placeholder={
                      selectedCompanyId
                        ? t`All locations`
                        : t`Select concept first`
                    }
                    disabled={isPrivilegedRoleSelection || !selectedCompanyId}
                    triggerClassName={`flex w-full items-center justify-between ${selectTriggerClass}`}
                    searchPlaceholder={t`Search locations…`}
                    emptyMessage={t`No locations found`}
                  />
                </div>
              </div>
            </SectionRow>

            <div className="flex items-center justify-end gap-3 bg-(--color-surface-content) p-5">
              <Button
                type="button"
                variant="outline"
                className="h-[52px] w-[200px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
                onClick={() => setOpen(false)}
              >
                {t`Cancel`}
              </Button>
              <Button
                type="submit"
                className="h-[52px] w-[200px] rounded-[10px] text-base font-medium"
                disabled={createUserMutation.isPending}
              >
                {createUserMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t`Create`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface EditUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
  roleAssignments: UserRoleAssignment[];
}

type EditUserFormState = {
  firstName: string;
  lastName: string;
  phone: string;
  birthDate: string;
  timezone: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  organizationId: string;
  companyId: string;
  locationId: string;
  roleId: string;
  status: "active" | "inactive";
};

/**
 * Radix mirrors a `Select` into a hidden native `<select>` whenever the field
 * sits inside a form. When the value changes before the dropdown has ever been
 * opened, that native element has no matching `<option>` yet, so the assignment
 * lands on "" and bubbles a spurious change back through `onValueChange` — which
 * is how a role seeded from a late-arriving assignment used to erase itself one
 * render later. None of these selects offers an empty choice, so an empty value
 * is never something the user picked.
 */
function onSelectValue(handler: (value: string) => void) {
  return (value: string) => {
    if (value === "") return;
    handler(value);
  };
}

/** ISO timestamp → `yyyy-MM-dd` for a native date input. */
function toDateInputValue(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

/** `yyyy-MM-dd` → midnight UTC ISO, which is what the API's ISO-8601 rule wants. */
function fromDateInputValue(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const date = new Date(`${trimmed}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/**
 * The picker offers the app's six US zones, but a user may already carry
 * something else (the API's own default is `America/Los_Angeles`). Keep whatever
 * is stored as an option so opening this dialog can't silently rewrite it.
 */
function timezoneOptions(current: string): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = APP_TIMEZONE_OPTIONS.map((zone) => ({
    value: zone.value,
    label: zone.label,
  }));
  if (current && !options.some((zone) => zone.value === current)) {
    options.unshift({ value: current, label: current });
  }
  return options;
}

/**
 * Digits are what the API stores, so that is what the form holds. The US mask is
 * cosmetic and only applies at exactly ten digits — anything longer is an
 * international number the mask would mangle.
 */
function displayPhoneNumber(digits: string): string {
  return digits.length === 10 ? formatUsPhone(digits) : digits;
}

function getInitialEditFormState(
  user: User,
  roleAssignments: UserRoleAssignment[],
  fallbackOrganizationId?: number,
): EditUserFormState {
  const currentRole = roleAssignments[0];
  const startsPrivileged = isPrivilegedRole(currentRole?.role);

  return {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    phone: sanitizePhoneNumber(user.phone ?? ""),
    birthDate: toDateInputValue(user.birthDate),
    timezone: user.timezone ?? "",
    addressLine1: user.addressLine1 ?? "",
    addressLine2: user.addressLine2 ?? "",
    city: user.city ?? "",
    state: user.state ?? "",
    postalCode: user.postalCode ?? "",
    country: user.country ?? "",
    organizationId: startsPrivileged ? "" : String(user.organization?.id ?? fallbackOrganizationId ?? ""),
    companyId:
      !startsPrivileged && currentRole?.scopeType === "COMPANY" && currentRole.scopeId != null
        ? String(currentRole.scopeId)
        : "",
    locationId:
      !startsPrivileged && currentRole?.scopeType === "LOCATION" && currentRole.scopeId != null
        ? String(currentRole.scopeId)
        : "",
    roleId: currentRole ? String(currentRole.role.id) : "",
    status: user.isActive ? "active" : "inactive",
  };
}

export function EditUserDialog({
  open,
  onOpenChange,
  user,
  roleAssignments,
}: EditUserDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const { user: authUser, hasRole, hasPermission } = useAuth();

  const isSuperAdmin = hasRole("SUPER_ADMIN");
  const isSupport = hasRole("SUPPORT");
  const canAssignAnyOrganization = isSuperAdmin || (isSupport && hasPermission("user:create"));
  const fixedOrganizationId = !canAssignAnyOrganization ? authUser?.organization?.id : undefined;

  const [formData, setFormData] = useState<EditUserFormState>(() =>
    getInitialEditFormState(user, roleAssignments, fixedOrganizationId),
  );
  const [orgDisplayName, setOrgDisplayName] = useState(() => user.organization?.name ?? "");
  const [locationDisplayName, setLocationDisplayName] = useState("");

  // Callers differ on when they fetch the user's roles: the detail page has them
  // before the dialog mounts, the user list starts the request only once the
  // dialog opens. Seeding once on open therefore left the list's copy of the
  // form showing "No role" — and saving from there quietly kept the old role,
  // which is what "you can't edit a user's roles" looked like. Re-seed whenever
  // the assignments actually change.
  const assignmentsKey = useMemo(
    () =>
      roleAssignments
        .map((assignment) => `${assignment.role.id}:${assignment.scopeType ?? ""}:${assignment.scopeId ?? ""}`)
        .join("|"),
    [roleAssignments],
  );

  const reseed = () => {
    setFormData(getInitialEditFormState(user, roleAssignments, fixedOrganizationId));
    setOrgDisplayName(user.organization?.name ?? "");
    setLocationDisplayName("");
  };

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevAssignmentsKey, setPrevAssignmentsKey] = useState(assignmentsKey);

  if (prevOpen !== open) {
    setPrevOpen(open);
    setPrevAssignmentsKey(assignmentsKey);
    if (open) {
      reseed();
    }
  } else if (open && prevAssignmentsKey !== assignmentsKey) {
    setPrevAssignmentsKey(assignmentsKey);
    reseed();
  }

  const selectedOrganizationId = useMemo(() => {
    if (formData.organizationId) {
      return Number(formData.organizationId);
    }
    return fixedOrganizationId;
  }, [fixedOrganizationId, formData.organizationId]);

  const selectedCompanyId = useMemo(() => {
    if (formData.companyId && formData.companyId !== "none") {
      return Number(formData.companyId);
    }

    return undefined;
  }, [formData.companyId]);

  const selectedLocationId = useMemo(() => {
    if (!selectedCompanyId) {
      return undefined;
    }

    if (formData.locationId) {
      return Number(formData.locationId);
    }

    return undefined;
  }, [formData.locationId, selectedCompanyId]);

  const { data: companies = [] } = useQuery({
    queryKey: ["organizations", selectedOrganizationId, "companies", "edit-user"],
    queryFn: () => dataApi.getOrganizationCompanies(selectedOrganizationId!),
    enabled: open && !!selectedOrganizationId,
  });

  const { data: roles = [] } = useQuery({
    queryKey: ["roles", "edit-user"],
    queryFn: () => dataApi.getRoles(),
    enabled: open,
  });

  const visibleRoles = roles;
  const selectedRole = useMemo(
    () => visibleRoles.find((role) => String(role.id) === formData.roleId),
    [formData.roleId, visibleRoles],
  );
  const isPrivilegedRoleSelection = isPrivilegedRole(selectedRole);
  const effectiveOrganizationId = isPrivilegedRoleSelection ? undefined : selectedOrganizationId;

  const sortedCompanies = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      await dataApi.updateUser(String(user.id), {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone,
        birthDate: fromDateInputValue(formData.birthDate),
        // Blank means "leave it alone" here — an empty timezone is not a state
        // the app can render, unlike an empty address line.
        timezone: formData.timezone || undefined,
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        postalCode: formData.postalCode.trim(),
        country: formData.country.trim(),
        organizationId: effectiveOrganizationId,
        isActive: formData.status === "active",
      });

      const selectedRoleId = formData.roleId ? Number(formData.roleId) : undefined;

      if (selectedRoleId) {
        const scopes: AssignRoleScopeInput[] | undefined = isPrivilegedRoleSelection
          ? undefined
          : selectedLocationId
            ? [{ scopeType: "LOCATION", scopeId: Number(selectedLocationId) }]
            : selectedCompanyId
              ? [{ scopeType: "COMPANY", scopeId: Number(selectedCompanyId) }]
              : effectiveOrganizationId
                ? [{ scopeType: "ORGANIZATION", scopeId: Number(effectiveOrganizationId) }]
                : undefined;

        await userApi.assignRole(String(user.id), String(selectedRoleId), scopes);
      }
    },
    onSuccess: async () => {
      const selectedCompany = companies.find((company) => String(company.id) === formData.companyId);
      const resolvedOrgName =
        orgDisplayName ||
        (fixedOrganizationId != null ? (authUser?.organization?.name ?? t`Organization`) : undefined);

      const scopeLabel = isPrivilegedRoleSelection
        ? t`Global scope`
        : selectedCompany
        ? `${t`Concept`}: ${selectedCompany.name}`
        : `${t`Organization`}: ${resolvedOrgName ?? t`Default scope`}`;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["users", Number(user.id)] }),
        queryClient.invalidateQueries({ queryKey: ["users", Number(user.id), "roles"] }),
        queryClient.invalidateQueries({ queryKey: ["users", Number(user.id), "locations"] }),
        queryClient.invalidateQueries({ queryKey: ["users", "all"] }),
      ]);

      toast.success(
        `${t`User updated successfully`} • ${t`Role`}: ${selectedRole?.name ?? t`Updated`} • ${scopeLabel}`,
      );
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || t`Failed to update user`);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.error(t`First name and last name are required`);
      return;
    }

    if (!isValidEmail(user.email ?? "")) {
      toast.error(t`Please enter a valid email address`);
      return;
    }

    if (!isValidPhoneNumber(formData.phone)) {
      toast.error(t`Phone number must contain only digits and be between 7 and 15 characters`);
      return;
    }

    if (!effectiveOrganizationId && !isPrivilegedRoleSelection) {
      toast.error(t`Organization is required`);
      return;
    }

    if (!formData.roleId) {
      toast.error(t`Role is required`);
      return;
    }

    saveMutation.mutate();
  };

  const inputClass = userDialogInputClass;
  const selectTriggerClass = userDialogSelectTriggerClass;
  const isSaving = saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideClose
        className="max-w-[900px] overflow-hidden rounded-(--radius-lg) border border-(--shell-border) p-0"
      >
        <form onSubmit={onSubmit} className="max-h-[90vh] overflow-y-auto bg-(--color-surface-content)">
          <div className="flex items-start justify-between border-b border-(--shell-border) p-5">
            <div>
              <DialogTitle className="text-2xl font-medium leading-none text-(--color-text-primary)">
                {t`Edit user`}
              </DialogTitle>
              <p className="mt-2 text-base text-(--color-text-secondary)">
                {t`Update this user's details, address, role, and scope.`}
              </p>
            </div>
            <DialogClose
              aria-label={t`Close dialog`}
              className="text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
            >
              <X className="h-6 w-6" />
            </DialogClose>
          </div>

          <SectionRow
            title={t`General details`}
            description={t`Basic personal details for this user.`}
          >
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-first-name">{t`First name`}</FormLabel>
                  <Input id="edit-user-first-name"
                    value={formData.firstName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                    placeholder={t`Enter first name...`}
                    className={inputClass}
                    autoFocus
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-last-name">{t`Last name`}</FormLabel>
                  <Input id="edit-user-last-name"
                    value={formData.lastName}
                    onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                    placeholder={t`Enter last name...`}
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-email">{t`Email`}</FormLabel>
                  <Input id="edit-user-email" value={user.email} disabled className={inputClass} />
                  <p className="text-sm text-(--color-text-secondary)">
                    {t`Email is the sign-in identity and cannot be changed here.`}
                  </p>
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-phone">{t`Phone`}</FormLabel>
                  <Input id="edit-user-phone"
                    value={displayPhoneNumber(formData.phone)}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, phone: sanitizePhoneNumber(e.target.value) }))
                    }
                    placeholder={t`(555) 555-5555`}
                    inputMode="tel"
                    autoComplete="tel"
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-birth-date">{t`Birth date`}</FormLabel>
                  <Input id="edit-user-birth-date"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, birthDate: e.target.value }))}
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-timezone">{t`Timezone`}</FormLabel>
                  <Select
                    value={formData.timezone}
                    onValueChange={onSelectValue((value) =>
                      setFormData((prev) => ({ ...prev, timezone: value })),
                    )}
                    disabled={isSaving}
                  >
                    <SelectTrigger id="edit-user-timezone" className={selectTriggerClass}>
                      <SelectValue placeholder={t`Select timezone`} />
                    </SelectTrigger>
                    <SelectContent>
                      {timezoneOptions(formData.timezone).map((zone) => (
                        <SelectItem key={zone.value} value={zone.value}>
                          {zone.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </SectionRow>

          <SectionRow
            title={t`Address`}
            description={t`Where this user is based. All optional.`}
          >
            <div className="space-y-5">
              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-address-line-1">{t`Address line 1`}</FormLabel>
                <Input id="edit-user-address-line-1"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData((prev) => ({ ...prev, addressLine1: e.target.value }))}
                  placeholder={t`Street address`}
                  autoComplete="address-line1"
                  className={inputClass}
                  disabled={isSaving}
                />
              </div>

              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-address-line-2">{t`Address line 2`}</FormLabel>
                <Input id="edit-user-address-line-2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData((prev) => ({ ...prev, addressLine2: e.target.value }))}
                  placeholder={t`Apartment, suite, unit`}
                  autoComplete="address-line2"
                  className={inputClass}
                  disabled={isSaving}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-city">{t`City`}</FormLabel>
                  <Input id="edit-user-city"
                    value={formData.city}
                    onChange={(e) => setFormData((prev) => ({ ...prev, city: e.target.value }))}
                    autoComplete="address-level2"
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-state">{t`State`}</FormLabel>
                  <Input id="edit-user-state"
                    value={formData.state}
                    onChange={(e) => setFormData((prev) => ({ ...prev, state: e.target.value }))}
                    autoComplete="address-level1"
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
                <div className="space-y-2">
                  <FormLabel htmlFor="edit-user-zip">{t`ZIP`}</FormLabel>
                  <Input id="edit-user-zip"
                    value={formData.postalCode}
                    onChange={(e) => setFormData((prev) => ({ ...prev, postalCode: e.target.value }))}
                    autoComplete="postal-code"
                    className={inputClass}
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-country">{t`Country`}</FormLabel>
                <Input id="edit-user-country"
                  value={formData.country}
                  onChange={(e) => setFormData((prev) => ({ ...prev, country: e.target.value }))}
                  autoComplete="country-name"
                  className={inputClass}
                  disabled={isSaving}
                />
              </div>
            </div>
          </SectionRow>

          <SectionRow
            title={t`Role configuration`}
            description={t`Assign role and account status.`}
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-role">{t`Role`}</FormLabel>
                <Select
                  value={formData.roleId}
                  disabled={isSaving}
                  onValueChange={onSelectValue((roleId) => {
                    const nextRole = visibleRoles.find((role) => String(role.id) === roleId);
                    const shouldClearScope = isPrivilegedRole(nextRole);

                    if (shouldClearScope) {
                      setOrgDisplayName("");
                      setLocationDisplayName("");
                    }

                    setFormData((prev) => ({
                      ...prev,
                      roleId,
                      organizationId: shouldClearScope ? "" : prev.organizationId,
                      companyId: shouldClearScope ? "" : prev.companyId,
                      locationId: shouldClearScope ? "" : prev.locationId,
                    }));
                  })}
                >
                  <SelectTrigger id="edit-user-role" className={selectTriggerClass}>
                    <SelectValue placeholder={t`Select role`} />
                  </SelectTrigger>
                  <SelectContent>
                    {visibleRoles.map((role) => (
                      <SelectItem key={role.id} value={String(role.id)}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-status">{t`Status`}</FormLabel>
                <Select
                  value={formData.status}
                  onValueChange={onSelectValue((value) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: value === "inactive" ? "inactive" : "active",
                    })))
                  }
                  disabled={isSaving}
                >
                  <SelectTrigger id="edit-user-status" className={selectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t`Active`}</SelectItem>
                    <SelectItem value="inactive">{t`Inactive`}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <FormLabel htmlFor="edit-user-organization">{t`Organization`}</FormLabel>
              {canAssignAnyOrganization ? (
                <OrganizationLookupSelect
                  triggerId="edit-user-organization"
                  value={formData.organizationId ? Number(formData.organizationId) : null}
                  adminScope={true}
                  displayValue={orgDisplayName || undefined}
                  onChange={(organization) => {
                    setOrgDisplayName(organization.name);
                    setLocationDisplayName("");
                    setFormData((prev) => ({
                      ...prev,
                      organizationId: String(organization.id),
                      companyId: "",
                      locationId: "",
                    }));
                  }}
                  placeholder={t`Select organization`}
                  searchPlaceholder={t`Search organizations…`}
                  emptyMessage={t`No organizations found`}
                  disabled={isSaving || isPrivilegedRoleSelection}
                  triggerClassName={selectTriggerClass}
                />
              ) : (
                <Select value={String(fixedOrganizationId ?? "none")} disabled>
                  <SelectTrigger id="edit-user-organization" className={selectTriggerClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={String(fixedOrganizationId ?? "none")}>
                      {authUser?.organization?.name ?? t`Organization unavailable`}
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </SectionRow>

          <SectionRow
            title={t`Access scope`}
            description={t`Define concept and location scope for this user.`}
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-concept">{t`Concept`}</FormLabel>
                <div className="relative">
                  <Select
                    value={formData.companyId || "none"}
                    disabled={isPrivilegedRoleSelection || !selectedOrganizationId || isSaving}
                    onValueChange={(value) => {
                      setLocationDisplayName("");
                      setFormData((prev) => ({
                        ...prev,
                        companyId: value === "none" ? "" : value,
                        locationId: "",
                      }));
                    }}
                  >
                    <SelectTrigger id="edit-user-concept" className={selectTriggerClass}>
                      <SelectValue
                        placeholder={
                          selectedOrganizationId
                            ? t`Select concept (optional)`
                            : t`Select organization first`
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t`Organization scope`}</SelectItem>
                      {sortedCompanies.map((company) => (
                        <SelectItem key={company.id} value={String(company.id)}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.companyId ? (
                    <button
                      type="button"
                      aria-label={t`Clear concept selection`}
                      className="absolute right-8 top-1/2 -translate-y-1/2 text-(--color-text-secondary) hover:text-(--color-text-primary)"
                      onClick={(event) => {
                        event.stopPropagation();
                        setLocationDisplayName("");
                        setFormData((prev) => ({
                          ...prev,
                          companyId: "",
                          locationId: "",
                        }));
                      }}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="space-y-2">
                <FormLabel htmlFor="edit-user-location">{t`Location`}</FormLabel>
                <VirtualSelectField<Location>
                  triggerId="edit-user-location"
                  queryKeyBase={["companies", selectedCompanyId, "locations", "edit-user"]}
                  buildQueryFn={(search) => (page, size) =>
                    locationApi.paginate({
                      page,
                      limit: size,
                      companyId: selectedCompanyId,
                      isActive: true,
                      search: search || undefined,
                    })
                  }
                  getItemValue={(l) => String(l.id)}
                  getItemLabel={(l) => l.name}
                  value={formData.locationId}
                  displayValue={locationDisplayName}
                  onChange={(value, location) => {
                    setLocationDisplayName(location.name);
                    setFormData((prev) => ({ ...prev, locationId: value }));
                  }}
                  onClear={() => {
                    setLocationDisplayName("");
                    setFormData((prev) => ({ ...prev, locationId: "" }));
                  }}
                  placeholder={
                    selectedCompanyId
                      ? t`All locations`
                      : t`Select concept first`
                  }
                  disabled={isPrivilegedRoleSelection || !selectedCompanyId || isSaving}
                  triggerClassName={`flex w-full items-center justify-between ${selectTriggerClass}`}
                  searchPlaceholder={t`Search locations…`}
                  emptyMessage={t`No locations found`}
                />
              </div>
            </div>
          </SectionRow>

          <div className="flex items-center justify-end gap-3 bg-(--color-surface-content) p-5">
            <Button
              type="button"
              variant="outline"
              className="h-[52px] w-[200px] rounded-[10px] border-(--shell-border) text-base font-medium text-(--color-text-primary)"
              onClick={() => onOpenChange(false)}
              disabled={isSaving}
            >
              {t`Cancel`}
            </Button>
            <Button
              type="submit"
              className="h-[52px] w-[200px] rounded-[10px] text-base font-medium"
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {isSaving ? t`Saving...` : t`Save`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
